import type { ReactNode } from 'react';

import { createElement, createRow, createSlide } from './factories';
import type {
  ElementType,
  Row,
  RowLayout,
  ScriptElement,
  Slide,
} from './schema.types';

/**
 * Pedagogical slide templates — quick-start scaffolds for common teaching
 * patterns. Each entry produces a complete `Slide` (rows + columns + seed
 * elements) the author can edit afterwards.
 *
 * Ported from the FULL_LMS_NEW frontend with three lean adaptations to this
 * repo's data model (see docs/new-lms/FRONTEND_OVERVIEW.md):
 *   1. `row()` sets `heightPercent` (this repo's Row model) rather than a px
 *      `height`. The design percents already sum to ~100 per slide.
 *   2. `slide()` calls `createSlide({ label })` (opts object signature here).
 *   3. `iconEl()` emits a `chip` — this repo has no `icon` element type — so
 *      icon-bearing templates render a near-equivalent labelled pill.
 *
 * Adding a new template:
 *   1. Append a `SlideTemplate` to `SLIDE_TEMPLATES` below.
 *   2. `build()` must return a Slide via `slide(label, rows)` — use the row()
 *      / el() / etc. helpers to keep the data terse.
 *   3. `preview` is a small JSX thumbnail shown in the new-slide picker modal.
 */

// ---------- categories ----------

export type TemplateCategory =
  | 'core-teaching'
  | 'practice'
  | 'reinforcement'
  | 'engagement';

export const TEMPLATE_CATEGORIES: { id: TemplateCategory; label: string }[] = [
  { id: 'core-teaching', label: 'Core Teaching' },
  { id: 'practice', label: 'Practice & Assessment' },
  { id: 'reinforcement', label: 'Reinforcement & Memory' },
  { id: 'engagement', label: 'Engagement & Interaction' },
];

export interface SlideTemplate {
  id: string;
  name: string;
  category: TemplateCategory;
  description: string;
  /** Tiny visual representation rendered in the picker grid. */
  preview: ReactNode;
  /** Materialise the template into a fresh `Slide` with unique ids. */
  build: () => Slide;
  /**
   * Kept in code but NOT shown in the picker. Flip to `false` / remove to
   * re-enable. Currently hides the assessment types we don't support yet
   * (subjective, fill-in-the-blanks, match-the-following, true/false).
   */
  hidden?: boolean;
}

// ---------- builder helpers ----------

function el(
  type: ElementType,
  content?: ScriptElement['content'],
  style?: ScriptElement['style'],
): ScriptElement {
  const e = createElement(type);
  if (content !== undefined) e.content = content;
  if (style) e.style = { ...e.style, ...style };
  return e;
}

function row(
  layout: RowLayout,
  heightDesignPercent: number,
  columnElements: ScriptElement[][] = [],
): Row {
  const r = createRow(layout);
  // This repo sizes rows by `heightPercent` (% of slide body). The template
  // design percents already sum to ~100 across a slide's rows.
  r.heightPercent = heightDesignPercent;
  columnElements.forEach((elements, i) => {
    if (r.columns[i]) r.columns[i].elements = elements;
  });
  return r;
}

function slide(label: string, rows: Row[]): Slide {
  return { ...createSlide({ label }), rows };
}

// ---------- shared element shorthands ----------

const title = (text: string) =>
  el('text', text, {
    fontSize: 36,
    fontWeight: 700,
    color: '#0f172a',
    textAlign: 'center',
  });

const body = (text: string, overrides?: ScriptElement['style']) =>
  el('text', text, { fontSize: 18, color: '#1f2937', ...(overrides ?? {}) });

const heading = (text: string, color = '#0f172a') =>
  el('text', text, { fontSize: 22, fontWeight: 700, color });

const chip = (label: string, bg = '#e0e7ff', color = '#3730a3') =>
  el('chip', { label }, { backgroundColor: bg, color });

const placeholderImage = (alt: string) =>
  el('image', {
    src: 'https://placehold.co/600x400/e0e7ff/64748b?text=Visual',
    alt,
  });

// This repo has no `icon` element type — render a labelled chip as a
// near-equivalent visual marker (lean-port substitution).
const iconEl = (id: string, caption?: string, color = '#4f46e5') =>
  el('chip', { label: caption ?? id.replace(/[-_]/g, ' ') }, {
    backgroundColor: '#eef2ff',
    color,
    textAlign: 'center',
  });

// ---------- templates ----------

export const SLIDE_TEMPLATES: SlideTemplate[] = [
  // ============ A. CORE TEACHING ============

  {
    id: 'concept-introduction',
    name: 'Concept Introduction',
    category: 'core-teaching',
    description: 'Title · Definition · Visual · Simple explanation',
    preview: previewStack([0.18, 0.18, 0.42, 0.22]),
    build: () =>
      slide('Concept Introduction', [
        row(1, 14, [[title('Concept Title')]]),
        row(1, 18, [
          [
            el(
              'text',
              'Definition: the precise statement of what this concept means.',
              { fontSize: 16, color: '#1e293b', backgroundColor: '#eef2ff', padding: 14, borderRadius: 8 },
            ),
          ],
        ]),
        row(1, 42, [[placeholderImage('Concept visual / diagram')]]),
        row(1, 26, [
          [
            body(
              'Simple explanation: describe the concept in plain language and connect it to something the learner already knows.',
            ),
          ],
        ]),
      ]),
  },

  {
    id: 'concept-example',
    name: 'Concept + Example',
    category: 'core-teaching',
    description: 'Concept on the left, worked example on the right, key takeaway below',
    preview: previewWith([
      { row: 0, h: 0.16 },
      { row: 1, h: 0.56, cols: [0.5, 0.5] },
      { row: 2, h: 0.28 },
    ]),
    build: () =>
      slide('Concept + Example', [
        row(1, 14, [[title('Title of the concept')]]),
        row(2, 56, [
          [
            heading('Concept', '#1d4ed8'),
            body('Explain the concept clearly here.', { fontSize: 16 }),
          ],
          [
            heading('Worked Example', '#047857'),
            body('Walk through one concrete example step by step.', { fontSize: 16 }),
          ],
        ]),
        row(1, 30, [
          [
            chip('Key takeaway', '#fef9c3', '#854d0e'),
            body('State the single most important thing to remember.', {
              fontSize: 15,
              color: '#0f172a',
            }),
          ],
        ]),
      ]),
  },

  {
    id: 'step-by-step',
    name: 'Step-by-Step Process',
    category: 'core-teaching',
    description: 'Numbered steps · Visual support · Final outcome',
    preview: previewWith([
      { row: 0, h: 0.14 },
      { row: 1, h: 0.6, cols: [0.33, 0.33, 0.34] },
      { row: 2, h: 0.26 },
    ]),
    build: () =>
      slide('Step-by-Step Process', [
        row(1, 14, [[title('Process Title')]]),
        row(3, 60, [
          [
            chip('Step 1', '#dbeafe', '#1d4ed8'),
            iconEl('step-by-step', 'Setup'),
            body('Describe step 1.', { fontSize: 14 }),
          ],
          [
            chip('Step 2', '#dbeafe', '#1d4ed8'),
            iconEl('next-step', 'Apply'),
            body('Describe step 2.', { fontSize: 14 }),
          ],
          [
            chip('Step 3', '#dbeafe', '#1d4ed8'),
            iconEl('correct', 'Verify'),
            body('Describe step 3.', { fontSize: 14 }),
          ],
        ]),
        row(1, 26, [
          [
            chip('Final outcome', '#dcfce7', '#166534'),
            body('What the learner should now be able to do or recognise.'),
          ],
        ]),
      ]),
  },

  {
    id: 'diagram-with-labels',
    name: 'Diagram with Labels',
    category: 'core-teaching',
    description: 'Large diagram on the left, numbered explanations on the right',
    preview: previewWith([
      { row: 0, h: 0.14 },
      { row: 1, h: 0.86, cols: [0.6, 0.4] },
    ]),
    build: () =>
      slide('Diagram with Labels', [
        row(1, 14, [[title('Diagram Title')]]),
        row(2, 86, [
          [placeholderImage('Annotated diagram')],
          [
            heading('Labels', '#0f172a'),
            body('1.  Label one — what it represents.', { fontSize: 15 }),
            body('2.  Label two — what it represents.', { fontSize: 15 }),
            body('3.  Label three — what it represents.', { fontSize: 15 }),
            body('4.  Label four — what it represents.', { fontSize: 15 }),
          ],
        ]),
      ]),
  },

  {
    id: 'comparison',
    name: 'Comparison Layout',
    category: 'core-teaching',
    description: 'Side-by-side A vs B with differences and similarities',
    preview: previewWith([
      { row: 0, h: 0.14 },
      { row: 1, h: 0.58, cols: [0.5, 0.5] },
      { row: 2, h: 0.28 },
    ]),
    build: () =>
      slide('Comparison: A vs B', [
        row(1, 14, [[title('A vs B')]]),
        row(2, 58, [
          [
            heading('Option A', '#1d4ed8'),
            placeholderImage('A visual'),
            body('Key feature 1', { fontSize: 14 }),
            body('Key feature 2', { fontSize: 14 }),
          ],
          [
            heading('Option B', '#b91c1c'),
            placeholderImage('B visual'),
            body('Key feature 1', { fontSize: 14 }),
            body('Key feature 2', { fontSize: 14 }),
          ],
        ]),
        row(2, 28, [
          [chip('Differences', '#fee2e2', '#991b1b'), body('How A and B diverge.', { fontSize: 14 })],
          [chip('Similarities', '#dcfce7', '#166534'), body('Where A and B overlap.', { fontSize: 14 })],
        ]),
      ]),
  },

  {
    id: 'cause-effect',
    name: 'Cause & Effect',
    category: 'core-teaching',
    description: 'Cause → Effect with a real-world example below',
    preview: previewWith([
      { row: 0, h: 0.14 },
      { row: 1, h: 0.5, cols: [0.42, 0.16, 0.42] },
      { row: 2, h: 0.36 },
    ]),
    build: () =>
      slide('Cause & Effect', [
        row(1, 14, [[title('Cause and Effect')]]),
        row(3, 50, [
          [
            chip('Cause', '#fee2e2', '#991b1b'),
            body('Describe the trigger or input.'),
          ],
          [iconEl('next-step', '→')],
          [
            chip('Effect', '#dcfce7', '#166534'),
            body('Describe the resulting outcome.'),
          ],
        ]),
        row(1, 36, [
          [
            chip('Real-world example', '#e0e7ff', '#3730a3'),
            body('A concrete situation where this cause-effect relationship plays out.'),
          ],
        ]),
      ]),
  },

  {
    id: 'analogy',
    name: 'Analogy-Based Teaching',
    category: 'core-teaching',
    description: 'Map a known concept onto a new one with a visual comparison',
    preview: previewWith([
      { row: 0, h: 0.14 },
      { row: 1, h: 0.46, cols: [0.5, 0.5] },
      { row: 2, h: 0.4 },
    ]),
    build: () =>
      slide('Analogy: Known → Unknown', [
        row(1, 14, [[title('Analogy Title')]]),
        row(2, 46, [
          [
            chip('Known concept', '#dbeafe', '#1d4ed8'),
            body('What the learner already understands.'),
          ],
          [
            chip('New concept', '#fef9c3', '#854d0e'),
            body('What we are introducing.'),
          ],
        ]),
        row(1, 40, [
          [
            heading('Mapping between the two', '#0f172a'),
            body('Known X → New Y · Known A → New B · Known P → New Q', {
              fontSize: 15,
            }),
          ],
        ]),
      ]),
  },

  {
    id: 'story',
    name: 'Story-Based Learning',
    category: 'core-teaching',
    description: 'Three scenes with characters and embedded learning',
    preview: previewWith([
      { row: 0, h: 0.14 },
      { row: 1, h: 0.86, cols: [0.33, 0.33, 0.34] },
    ]),
    build: () =>
      slide('Story-Based Learning', [
        row(1, 14, [[title('Story Title')]]),
        row(3, 86, [
          [
            chip('Scene 1', '#fce7f3', '#9d174d'),
            placeholderImage('Scene one'),
            body('Setup of the story.', { fontSize: 14 }),
          ],
          [
            chip('Scene 2', '#fce7f3', '#9d174d'),
            placeholderImage('Scene two'),
            body('The conflict / learning moment.', { fontSize: 14 }),
          ],
          [
            chip('Scene 3', '#fce7f3', '#9d174d'),
            placeholderImage('Scene three'),
            body('Resolution and lesson.', { fontSize: 14 }),
          ],
        ]),
      ]),
  },

  // ============ B. PRACTICE & ASSESSMENT ============

  {
    id: 'mcq',
    name: 'MCQ',
    category: 'practice',
    description: 'Question · Four options',
    preview: previewWith([
      { row: 0, h: 0.3 },
      { row: 1, h: 0.7 },
    ]),
    build: () =>
      slide('Multiple Choice', [
        row(1, 28, [
          [
            chip('Question', '#dbeafe', '#1d4ed8'),
            body('Type your question here.', { fontSize: 22, fontWeight: 600 }),
          ],
        ]),
        row(1, 72, [
          [
            el('mcq', {
              question: 'Type your question here.',
              options: ['Option A', 'Option B', 'Option C', 'Option D'],
              answerIndex: 0,
            }),
          ],
        ]),
      ]),
  },

  {
    id: 'subjective',
    hidden: true,
    name: 'Subjective Question',
    category: 'practice',
    description: 'Question with writing space and a hint section',
    preview: previewWith([
      { row: 0, h: 0.32 },
      { row: 1, h: 0.5 },
      { row: 2, h: 0.18 },
    ]),
    build: () =>
      slide('Subjective Question', [
        row(1, 30, [
          [
            chip('Question', '#dbeafe', '#1d4ed8'),
            body('Write the open-ended question here.', { fontSize: 22, fontWeight: 600 }),
          ],
        ]),
        row(1, 54, [
          [
            el(
              'text',
              '✍️  Writing space — students answer here…',
              {
                fontSize: 14,
                color: '#94a3b8',
                backgroundColor: '#f8fafc',
                padding: 24,
                borderRadius: 8,
              },
            ),
          ],
        ]),
        row(1, 16, [
          [
            chip('Hint', '#fef9c3', '#854d0e'),
            body('Optional hint to guide the learner.', { fontSize: 13 }),
          ],
        ]),
      ]),
  },

  {
    id: 'fill-in-blanks',
    hidden: true,
    name: 'Fill in the Blanks',
    category: 'practice',
    description: 'Sentence with highlighted blanks',
    preview: previewWith([
      { row: 0, h: 0.3 },
      { row: 1, h: 0.5 },
      { row: 2, h: 0.2 },
    ]),
    build: () =>
      slide('Fill in the Blanks', [
        row(1, 24, [[title('Fill in the blanks')]]),
        row(1, 56, [
          [
            body(
              'The capital of France is __________ and its currency is the __________.',
              { fontSize: 22, fontWeight: 500, textAlign: 'center' },
            ),
          ],
        ]),
        row(1, 20, [
          [chip('Hint', '#fef9c3', '#854d0e'), body('Optional hint or clue.', { fontSize: 13 })],
        ]),
      ]),
  },

  {
    id: 'match-following',
    hidden: true,
    name: 'Match the Following',
    category: 'practice',
    description: 'Two columns of items to match',
    preview: previewWith([
      { row: 0, h: 0.18 },
      { row: 1, h: 0.82, cols: [0.5, 0.5] },
    ]),
    build: () =>
      slide('Match the Following', [
        row(1, 16, [[title('Match the following')]]),
        row(2, 84, [
          [
            chip('Column A', '#dbeafe', '#1d4ed8'),
            body('1.  Item one', { fontSize: 16 }),
            body('2.  Item two', { fontSize: 16 }),
            body('3.  Item three', { fontSize: 16 }),
            body('4.  Item four', { fontSize: 16 }),
          ],
          [
            chip('Column B', '#fef9c3', '#854d0e'),
            body('a.  Match one', { fontSize: 16 }),
            body('b.  Match two', { fontSize: 16 }),
            body('c.  Match three', { fontSize: 16 }),
            body('d.  Match four', { fontSize: 16 }),
          ],
        ]),
      ]),
  },

  {
    id: 'true-false',
    hidden: true,
    name: 'True / False',
    category: 'practice',
    description: 'A statement with True / False toggle',
    preview: previewWith([
      { row: 0, h: 0.4 },
      { row: 1, h: 0.4 },
      { row: 2, h: 0.2 },
    ]),
    build: () =>
      slide('True / False', [
        row(1, 38, [
          [
            chip('Statement', '#e0e7ff', '#3730a3'),
            body('The statement learners must judge.', { fontSize: 22, fontWeight: 600 }),
          ],
        ]),
        row(1, 42, [
          [
            el('mcq', {
              question: 'Is the statement above True or False?',
              options: ['True', 'False'],
              answerIndex: 0,
            }),
          ],
        ]),
        row(1, 20, [
          [chip('Why?', '#fef9c3', '#854d0e'), body('Brief reason / explanation.', { fontSize: 13 })],
        ]),
      ]),
  },

  // ============ C. REINFORCEMENT & MEMORY ============

  {
    id: 'key-takeaways',
    name: 'Key Takeaways',
    category: 'reinforcement',
    description: 'Bullet points with recall icons',
    preview: previewWith([
      { row: 0, h: 0.18 },
      { row: 1, h: 0.82, cols: [0.5, 0.5] },
    ]),
    build: () =>
      slide('Key Takeaways', [
        row(1, 16, [[title('Key Takeaways')]]),
        row(2, 84, [
          [
            iconEl('idea', 'Insight 1'),
            body('Main point one — concise statement.'),
            iconEl('important', 'Watch out'),
            body('Main point two — concise statement.'),
          ],
          [
            iconEl('connection', 'Connect'),
            body('Main point three — concise statement.'),
            iconEl('correct', 'Remember'),
            body('Main point four — concise statement.'),
          ],
        ]),
      ]),
  },

  {
    id: 'summary-flow',
    name: 'Summary Flow',
    category: 'reinforcement',
    description: 'Visual flow of the topic in 3–4 stages',
    preview: previewWith([
      { row: 0, h: 0.18 },
      { row: 1, h: 0.82, cols: [0.22, 0.06, 0.22, 0.06, 0.22, 0.06, 0.16] },
    ]),
    build: () =>
      slide('Summary Flow', [
        row(1, 16, [[title('Summary Flow')]]),
        row(3, 84, [
          [
            chip('Start', '#dbeafe', '#1d4ed8'),
            body('Concept introduced.', { fontSize: 14 }),
          ],
          [
            chip('Then', '#e0e7ff', '#3730a3'),
            body('Key idea developed.', { fontSize: 14 }),
          ],
          [
            chip('End', '#dcfce7', '#166534'),
            body('Outcome / takeaway.', { fontSize: 14 }),
          ],
        ]),
      ]),
  },

  {
    id: 'flashcard',
    name: 'Flashcard Style',
    category: 'reinforcement',
    description: 'Front: question, Back: answer',
    preview: previewWith([
      { row: 0, h: 0.18 },
      { row: 1, h: 0.4 },
      { row: 2, h: 0.42 },
    ]),
    build: () =>
      slide('Flashcard', [
        row(1, 16, [[title('Flashcard')]]),
        row(1, 42, [
          [
            chip('Front · Question', '#dbeafe', '#1d4ed8'),
            body('What is the question?', { fontSize: 24, fontWeight: 600, textAlign: 'center' }),
          ],
        ]),
        row(1, 42, [
          [
            chip('Back · Answer', '#dcfce7', '#166534'),
            body('Reveal the answer here.', { fontSize: 22, textAlign: 'center' }),
          ],
        ]),
      ]),
  },

  {
    id: 'revision-sheet',
    name: 'Revision Sheet',
    category: 'reinforcement',
    description: 'Compact multi-point summary with formulas / keywords',
    preview: previewWith([
      { row: 0, h: 0.12 },
      { row: 1, h: 0.88, cols: [0.5, 0.5] },
    ]),
    build: () =>
      slide('Revision Sheet', [
        row(1, 12, [[title('Revision Sheet')]]),
        row(2, 88, [
          [
            chip('Key Points', '#dbeafe', '#1d4ed8'),
            body('• Point one', { fontSize: 14 }),
            body('• Point two', { fontSize: 14 }),
            body('• Point three', { fontSize: 14 }),
            body('• Point four', { fontSize: 14 }),
          ],
          [
            chip('Formulas / Keywords', '#fef9c3', '#854d0e'),
            el('latex', { source: 'a^2 + b^2 = c^2', displayMode: true }),
            body('Keyword one — definition.', { fontSize: 13 }),
            body('Keyword two — definition.', { fontSize: 13 }),
          ],
        ]),
      ]),
  },

  // ============ D. ENGAGEMENT & INTERACTION ============

  {
    id: 'think-answer',
    name: 'Think & Answer',
    category: 'engagement',
    description: 'Prompt with a pause-and-think indicator',
    preview: previewWith([
      { row: 0, h: 0.3 },
      { row: 1, h: 0.5 },
      { row: 2, h: 0.2 },
    ]),
    build: () =>
      slide('Think & Answer', [
        row(1, 28, [
          [
            chip('Prompt', '#e0e7ff', '#3730a3'),
            body('Pose the question for learners to think about.', {
              fontSize: 22,
              fontWeight: 600,
              textAlign: 'center',
            }),
          ],
        ]),
        row(1, 52, [[iconEl('thinking', 'Take a moment…')]]),
        row(1, 20, [
          [
            body('⏳  Pause and discuss with a partner before continuing.', {
              fontSize: 13,
              textAlign: 'center',
              color: '#64748b',
            }),
          ],
        ]),
      ]),
  },

  {
    id: 'predict-outcome',
    name: 'Predict the Outcome',
    category: 'engagement',
    description: 'Scenario · Options · Reveal',
    preview: previewWith([
      { row: 0, h: 0.28 },
      { row: 1, h: 0.42, cols: [0.33, 0.33, 0.34] },
      { row: 2, h: 0.3 },
    ]),
    build: () =>
      slide('Predict the Outcome', [
        row(1, 26, [
          [
            chip('Scenario', '#fef9c3', '#854d0e'),
            body('Describe the setup learners must reason about.', {
              fontSize: 18,
              fontWeight: 500,
            }),
          ],
        ]),
        row(3, 44, [
          [chip('Option A', '#dbeafe', '#1d4ed8'), body('Possible outcome A.', { fontSize: 14 })],
          [chip('Option B', '#dbeafe', '#1d4ed8'), body('Possible outcome B.', { fontSize: 14 })],
          [chip('Option C', '#dbeafe', '#1d4ed8'), body('Possible outcome C.', { fontSize: 14 })],
        ]),
        row(1, 30, [
          [
            chip('Reveal', '#dcfce7', '#166534'),
            body('Reveal what actually happens and why.', { fontSize: 14 }),
          ],
        ]),
      ]),
  },

  {
    id: 'spot-mistake',
    name: 'Spot the Mistake',
    category: 'engagement',
    description: 'Incorrect example with an area to highlight',
    preview: previewWith([
      { row: 0, h: 0.18 },
      { row: 1, h: 0.5 },
      { row: 2, h: 0.32 },
    ]),
    build: () =>
      slide('Spot the Mistake', [
        row(1, 16, [[title('Spot the mistake')]]),
        row(1, 50, [
          [
            chip('Incorrect example', '#fee2e2', '#991b1b'),
            body(
              '2 + 2 × 5 = 20 — find what is wrong with this working.',
              {
                fontSize: 22,
                fontWeight: 500,
                color: '#1f2937',
                backgroundColor: '#fff1f2',
                padding: 16,
                borderRadius: 8,
              },
            ),
          ],
        ]),
        row(1, 34, [
          [
            chip('Highlight & explain', '#fef9c3', '#854d0e'),
            body(
              'Use the highlight tool in Teach Mode to point at the error and narrate the correction.',
              { fontSize: 14 },
            ),
          ],
        ]),
      ]),
  },
];

// ---------- preview thumbnail builders ----------

/**
 * Build a vertical-stack thumbnail. `heights` is an array of fractions of the
 * card height (must sum to ~1.0). All rows are full-width.
 */
function previewStack(heights: number[]): ReactNode {
  return (
    <div className="flex h-full w-full flex-col gap-1 p-2">
      {heights.map((h, i) => (
        <div
          key={i}
          className="rounded-sm bg-fg/15"
          style={{ flex: h }}
        />
      ))}
    </div>
  );
}

/**
 * Build a richer thumbnail where each row can split into multiple columns.
 * `rows` describes height fraction + optional column fractions.
 */
function previewWith(
  rows: { row: number; h: number; cols?: number[] }[],
): ReactNode {
  return (
    <div className="flex h-full w-full flex-col gap-1 p-2">
      {rows.map((r) => (
        <div key={r.row} className="flex gap-1" style={{ flex: r.h }}>
          {(r.cols ?? [1]).map((c, i) => (
            <div key={i} className="rounded-sm bg-fg/15" style={{ flex: c }} />
          ))}
        </div>
      ))}
    </div>
  );
}
